<template>
  <div
    class="track-mixer"
    :class="{ 'track-mixer-muted': mixTrack.actualMute }"
    :style="style"
  >
    <audio-meter class="name" :value="meter">{{ track.name }}</audio-meter>
    <button
      @click="toggleMute"
      class="btn mute"
      title="Mute channel"
      :class="{ 'mute-selected': mixTrack.muted }"
    >
      M
    </button>
    <button
      @click="toggleSolo"
      class="btn solo"
      title="Solo channel"
      :class="{ 'solo-selected': mixTrack.solo }"
    >
      S
    </button>
    <div class="pan" title="Channel panning">
      <knob :value="mixTrack.pan" @input="onPanChange" />
    </div>
    <div class="width" title="Channel width">
      <knob
        :value="mixTrack.width"
        :min="0"
        :max="1"
        :reset="1"
        :is-width="true"
        @input="onWidthChange"
      />
    </div>
    <div class="volume">
      <input
        type="range"
        min="0"
        max="130"
        :value="volume"
        @input="onVolumeChange"
      />
    </div>
    <div v-if="toDelete" class="delete">
      <trash-icon />
    </div>
  </div>
</template>

<script lang="ts">
import { Track } from "@/models";
import { MixTrackExt } from "@/store/mixes";
import { Options, Vue } from "vue-class-component";
import { mapActions, mapGetters, mapState } from "vuex";
import AudioMeter from "./AudioMeter.vue";
import Knob from "./Knob.vue";
import { TrashIcon } from "@heroicons/vue/24/outline";

@Options({
  components: {
    AudioMeter,
    TrashIcon,
    Knob,
  },
  props: {
    mixTrack: {
      type: Object,
      required: true,
    },
    toDelete: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    ...mapActions("mixes", ["patchMixTrack"]),
  },
  computed: {
    ...mapGetters("projects", ["trackMap"]),
    ...mapState("mixes", ["trackHeight"]),
  },
})
export default class TrackMixer extends Vue {
  mixTrack!: MixTrackExt;
  trackMap!: Record<string, Track>;
  trackHeight!: number;
  toDelete!: boolean;

  patchMixTrack!: (data: {
    mixTrackId: string;
    payload: Partial<MixTrackExt>;
  }) => Promise<void>;

  get track() {
    return this.trackMap[this.mixTrack.track_id];
  }

  get style() {
    return { height: `${this.trackHeight}px` };
  }

  get meter() {
    return this.mixTrack.channel?.meter || 0;
  }

  get volume() {
    return this.mixTrack.volume * 100;
  }

  adjustMixTrackValue<K extends keyof MixTrackExt>(
    key: K,
    value: MixTrackExt[K]
  ) {
    const payload = { [key]: value };
    const mixTrackId = this.mixTrack._id;
    this.patchMixTrack({ mixTrackId, payload });
  }

  onVolumeChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = target.valueAsNumber;
    this.adjustMixTrackValue("volume", value / 100);
  }

  onPanChange(pan: number) {
    this.adjustMixTrackValue("pan", pan);
  }

  onWidthChange(width: number) {
    this.adjustMixTrackValue("width", width);
  }

  toggleMute() {
    this.adjustMixTrackValue("muted", !this.mixTrack.muted);
  }
  toggleSolo() {
    this.adjustMixTrackValue("solo", !this.mixTrack.solo);
  }
}
</script>

<style lang="scss">
.track-mixer {
  height: 50px;
  width: 300px;
  box-sizing: border-box;
  overflow: hidden;
  border-bottom: 1px solid #ccc;
  background: linear-gradient(180deg, #f0f0f0 0%, #d0d0d0 100%);
  display: grid;
  padding: 4px;
  user-select: none;
  // position: relative;
  grid-template-areas:
    "name    .     mute  solo"
    "volume  .     pan   width";
  grid-gap: 2px;
  grid-template-rows: 20px 20px;
  grid-template-columns: auto 1px 20px 20px;

  &.track-mixer-muted {
    background: linear-gradient(180deg, #c0c0c0 0%, #b0b0b0 100%);
  }

  .name {
    grid-area: name;
    cursor: grab;
  }
  .pan {
    grid-area: pan;
  }
  .width {
    grid-area: width;
  }

  .btn.mute,
  .btn.solo {
    background: transparent;
    color: #000;
    line-height: 1em;
    width: 20px;
    height: 20px;
    font-size: 1em;
  }

  .mute.mute-selected {
    border-color: #d00;
    text-shadow: 1px 0 2px #d00, -1px 0 2px #d00, 0 1px 2px #d00,
      0 -1px 2px #d00;
    color: white;
  }

  .solo.solo-selected {
    border-color: #ff4;
    color: #000;
    text-shadow: 1px 0 2px #ff4, -1px 0 2px #ff4, 0 1px 2px #ff4,
      0 -1px 2px #ff4;
  }

  .mute {
    grid-area: mute;
  }
  .solo {
    grid-area: solo;
  }

  .delete {
    position: absolute;
    top: 5px;
    left: calc(50% - 24px);
    width: 40px;
    opacity: 0.3;
  }

  .volume {
    grid-area: volume;
    padding-top: 2px;

    // https://www.cssportal.com/style-input-range/
    input[type="range"] {
      -webkit-appearance: none;
      background: transparent;
      width: calc(100% - 2px);
    }
    input[type="range"]:focus {
      outline: none;
    }
    input[type="range"]::-webkit-slider-runnable-track {
      width: 100%;
      height: 6px;
      cursor: pointer;
      animate: 0.2s;
      box-shadow: 0px 0px 0px #000000;
      background: #b6b6b6;
      border-radius: 25px;
      border: 1px solid #8a8a8a;
    }
    input[type="range"]::-webkit-slider-thumb {
      box-shadow: 1px 1px 1px #828282;
      border: 1px solid #8a8a8a;
      height: 16px;
      width: 16px;
      border-radius: 4px;
      background: #dadada;
      cursor: pointer;
      -webkit-appearance: none;
      margin-top: -6px;
    }
    input[type="range"]:focus::-webkit-slider-runnable-track {
      background: #b6b6b6;
    }
    input[type="range"]::-moz-range-track {
      box-sizing: border-box;
      width: 100%;
      height: 6px;
      cursor: pointer;
      animate: 0.2s;
      box-shadow: 0px 0px 0px #000000;
      background: #b6b6b6;
      border-radius: 25px;
      border: 1px solid #8a8a8a;
    }
    input[type="range"]::-moz-range-thumb {
      box-sizing: border-box;
      box-shadow: 1px 1px 1px #828282;
      border: 1px solid #8a8a8a;
      height: 16px;
      width: 16px;
      border-radius: 4px;
      background: #dadada;
      cursor: pointer;
    }
    input[type="range"]::-ms-track {
      width: 100%;
      height: 6px;
      cursor: pointer;
      animate: 0.2s;
      background: transparent;
      border-color: transparent;
      color: transparent;
    }
    input[type="range"]::-ms-fill-lower {
      background: #b6b6b6;
      border: 1px solid #8a8a8a;
      border-radius: 50px;
      box-shadow: 0px 0px 0px #000000;
    }
    input[type="range"]::-ms-fill-upper {
      background: #b6b6b6;
      border: 1px solid #8a8a8a;
      border-radius: 50px;
      box-shadow: 0px 0px 0px #000000;
    }
    input[type="range"]::-ms-thumb {
      margin-top: 1px;
      box-shadow: 1px 1px 1px #828282;
      border: 1px solid #8a8a8a;
      height: 16px;
      width: 16px;
      border-radius: 4px;
      background: #dadada;
      cursor: pointer;
    }
    input[type="range"]:focus::-ms-fill-lower {
      background: #b6b6b6;
    }
    input[type="range"]:focus::-ms-fill-upper {
      background: #b6b6b6;
    }
  }
}
</style>
